import React, { useEffect, useRef, useState } from "react";
import { HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineDocumentText, HiOutlineExternalLink } from "react-icons/hi";
import { pageLabel, sourceName } from "../../../../helpers/utils/citation-utils";
import "./index.css";

// Long enough to judge relevance, short enough that a few sources still fit
// in the widget. The rest is one click away.
const PLAIN_CLAMP_CHARS = 280;

/**
 * The sources an answer was drawn from (`metadata.retrievedContext`), numbered
 * as the answer cites them inline.
 *
 * Collapsed by default: the answer is what was asked for, the sources are what
 * a reader turns to when they doubt it. `focus` ({ number, nonce }) is set when
 * the reader clicks an inline [n]: the list opens and source n is scrolled to,
 * highlighted and opened with the text around it. The nonce makes a second
 * click on the same citation act again.
 */
export default function RetrievedContext({ context, focus }) {
  const [expanded, setExpanded] = useState(false);
  const entryRefs = useRef([]);
  const focusedIndex = focus ? focus.number - 1 : -1;

  useEffect(() => {
    if (focusedIndex >= 0) {
      setExpanded(true);
    }
  }, [focusedIndex, focus?.nonce]);

  useEffect(() => {
    if (!expanded || focusedIndex < 0) {
      return;
    }
    const element = entryRefs.current[focusedIndex];
    // Not every environment implements scrollIntoView (jsdom does not).
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [expanded, focusedIndex, focus?.nonce]);

  if (!Array.isArray(context) || context.length === 0) {
    return null;
  }

  // Documents, not passages: the runtime groups one document's passages into
  // one entry, so this counts what the answer rests on.
  const label = context.length === 1 ? "1 source" : `${context.length} sources`;

  return (
    <div className="chat-sources">
      <button type="button" className="chat-sources-toggle" onClick={() => setExpanded(open => !open)} aria-expanded={expanded}>
        {expanded ? <HiOutlineChevronDown size={13} aria-hidden="true" /> : <HiOutlineChevronRight size={13} aria-hidden="true" />}
        <HiOutlineDocumentText size={13} aria-hidden="true" />
        {label}
      </button>

      {expanded && (
        <ul className="chat-sources-list">
          {context.map((entry, index) => (
            <SourceEntry
              key={`${entry.sourceId || "source"}-${index}`}
              entry={entry}
              index={index}
              focused={index === focusedIndex}
              ref={element => { entryRefs.current[index] = element; }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

const SourceEntry = React.forwardRef(function SourceEntry({ entry, index, focused }, ref) {
  const [showAll, setShowAll] = useState(false);

  // Reached from an inline [n]: the reader wants to check that claim, so the
  // cited text opens with what surrounds it.
  useEffect(() => {
    if (focused) {
      setShowAll(true);
    }
  }, [focused]);

  const passages = entry.passageCount || 1;
  // Where in the document to look. Only PDFs have pages.
  const pages = pageLabel(entry);
  const sections = Array.isArray(entry.sections) ? entry.sections.filter(s => s && s.cited) : [];
  // A web page is cited by its address and opens directly. An uploaded file
  // does not: the widget's runtime key cannot reach the document preview API.
  const url = /^https?:\/\//i.test(entry.sourceId || "") ? entry.sourceId : null;

  return (
    <li ref={ref} className={`chat-source${focused ? " is-focused" : ""}`} data-focused={focused ? "true" : undefined}>
      <div className="chat-source-head">
        {/* The same number the answer cites inline. */}
        <span className="chat-source-num">[{index + 1}]</span>
        <span className="chat-source-name">{sourceName(entry)}</span>
        {pages && <span className="chat-source-meta">{pages}</span>}
        {passages > 1 && <span className="chat-source-meta">{passages} passages</span>}
        {url && (
          <a className="chat-source-link" href={url} target="_blank" rel="noopener noreferrer">
            <HiOutlineExternalLink size={12} aria-hidden="true" />
            Open
          </a>
        )}
      </div>

      {sections.length > 0 ? (
        <Sections sections={sections} showAll={showAll} onToggle={() => setShowAll(open => !open)} />
      ) : (
        <PlainContent content={entry.content} showAll={showAll} onToggle={() => setShowAll(open => !open)} />
      )}
    </li>
  );
});

/**
 * Each cited passage, highlighted, in the text around it in the document.
 * Collapsed, only the cited text shows; expanded, the lines before and after
 * it too, so a heading-sized chunk reads as the clause it belongs to.
 */
function Sections({ sections, showAll, onToggle }) {
  const hasSurrounding = sections.some(s => s.before || s.after);
  return (
    <>
      {sections.map((section, i) => (
        <p key={i} className="chat-source-text">
          {showAll && section.before && <>{section.before}{"\n"}</>}
          <mark data-cited="true">{section.cited}</mark>
          {showAll && section.after && <>{"\n"}{section.after}</>}
        </p>
      ))}
      {hasSurrounding && (
        <button type="button" className="chat-source-more" onClick={onToggle}>
          {showAll ? "Hide surrounding text" : "Show surrounding text"}
        </button>
      )}
    </>
  );
}

/** A source without sections (older runtimes): its passages as one block. */
function PlainContent({ content, showAll, onToggle }) {
  const text = typeof content === "string" ? content : "";
  if (!text) {
    return null;
  }
  const long = text.length > PLAIN_CLAMP_CHARS;
  return (
    <>
      <p className="chat-source-text">{showAll || !long ? text : `${text.slice(0, PLAIN_CLAMP_CHARS)}…`}</p>
      {long && (
        <button type="button" className="chat-source-more" onClick={onToggle}>
          {showAll ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}
